import { Button, Formatters, Icon } from '@keoworld/gbl-ui-kit'
import styled, { css } from 'styled-components'
import { kilobyteConverter } from '../utils/functions/kilobytes-translator'
import { useRef } from 'react'

/**
 * @param {DocumentType} props
 * @returns
 */
const InputFile = (props) => {
  const {
    allowedDocumentTypes,
    allowUpload = true,
    className,
    documentName,
    isDisabled,
    file,
    label,
    onChange,
    onClickDelete,
    onClickDownload,
    onClickComment,
    warning,
    deleteFile
  } = props

  const inputElement = useRef()

  const uploadFile = () => inputElement.current.click()

  const onHandleChange = (event) => {
    const files = event.target.files
    if (files && onChange) onChange(files)
  }

  return (
    <DocumentStyled
      className={className}
      isThereFile={Boolean(file)}
      warning={warning && !file}
    >
      <div>
        <div className='document-label'>{label}</div>
        {file && file.name && (
          <div className='document'>
            <div className='file-name'>
              <Icon name='attach_file' />
              <span>{file.name}</span>
            </div>
            {file?.size && (
              <div className='file-size'>
                {Formatters.numberFormat(
                  kilobyteConverter(file.size, 'MB'),
                  'es-MX',
                  {
                    maximumFractionDigits: 2
                  }
                )}{' '}
                MB
              </div>
            )}
          </div>
        )}
      </div>
      <input
        ref={inputElement}
        id={documentName}
        name={documentName}
        accept={allowedDocumentTypes}
        filename={file?.name}
        onChange={onHandleChange}
        type='file'
        value=''
        hidden
      />
      <div className='action-section'>
        <div className='icon-group'>
          {typeof onClickDownload === 'function' && file?.id && (
            <button onClick={onClickDownload} disabled={isDisabled}>
              <Icon name='file_download' type='outlined' />
            </button>
          )}
          {typeof onClickComment === 'function' && (
            <button onClick={onClickComment}>
              <Icon name='add_comment' type='outlined' />
            </button>
          )}
          {typeof onClickDelete === 'function' &&
            file &&
            deleteFile &&
            file?.name && (
              <button onClick={onClickDelete} disabled={isDisabled}>
                <Icon name='delete' type='outlined' />
              </button>
            )}
        </div>
        {allowUpload && !file && (
          <Button
            disabled={isDisabled}
            onClick={uploadFile}
            buttonType={'light'}
          >
            {file ? 'Update File' : 'Upload File'}
          </Button>
        )}
      </div>
    </DocumentStyled>
  )
}

const DocumentStyled = styled.div`
  margin: 0.25rem 0;
  padding: 10px;
  min-height: 50px;
  min-width: 58em;
  width: 100%;
  border: 1px solid
    ${({ theme }) => theme.colors.grayShades[300].backgroundColor};
  border-radius: 8px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  .document-label {
    text-transform: capitalize;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 1rem;
    max-width: 44em;
  }
  button {
    height: 25px;
  }

  .action-section {
    display: flex;
  }

  .icon-group {
    display: flex;
    justify-content: flex-end;
  }

  ${({ isThereFile }) => {
    if (isThereFile) {
      return css`
        position: relative;
        min-height: 5.5em;
        padding: 1rem;

        .document {
          .file-name {
            display: flex;
            align-items: center;
            font-size: 9pt;
            font-weight: 600;
            .icon {
              font-size: 15px;
            }
          }
          .file-size {
            margin-left: 20px;
            opacity: 30%;
            font-size: 8pt;
          }
        }

        .icon-group {
          button {
            cursor: pointer;
            border: none;
            background-color: transparent;

            height: 30px;
            width: 40px;
            .icon {
              font-size: 20px;
            }
          }
        }
      `
    }
  }}

  ${({ warning, theme }) => {
    if (warning)
      return css`
        border-color: ${theme.alertTheme.error.backgroundColor};
      `
  }}
`

export default InputFile

/**
 * @typedef DocumentType
 * @property {string} allowedDocumentTypes
 * @property {boolean} allowUpload
 * @property {string} className
 * @property {string} documentName
 * @property {boolean} isDisabled
 * @property {FileObject} file
 * @property {string} label
 * @property {Function} onChange
 * @property {Function} onClickDelete
 * @property {Function} onClickDownload
 * @property {Function} onClickComment
 * @property {boolean} warning
 */

/**
 * @typedef FileObject
 * @property {string} name
 * @property {number} size
 */
